import { getProxy } from './proxyManager';

// Capture at 2x retina resolution for crisp filmstrip
const THUMB_HEIGHT = 96;
const THUMB_INTERVAL_SEC = 0.5; // two thumbnails per second for dense filmstrip

/** In-memory cache: assetId → array of thumbnails */
const thumbCache = new Map<string, ImageBitmap[]>();

/**
 * Generate a filmstrip of thumbnails from the proxy video.
 * Uses a hidden <video> element + offscreen canvas to capture frames.
 */
export async function generateThumbnails(assetId: string): Promise<ImageBitmap[]> {
  const cached = thumbCache.get(assetId);
  if (cached) return cached;

  const proxyData = await getProxy(assetId);
  if (!proxyData) return [];

  const blob = new Blob([proxyData], { type: 'video/mp4' });
  const url = URL.createObjectURL(blob);

  try {
    const thumbs = await extractFrames(url);
    thumbCache.set(assetId, thumbs);
    return thumbs;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function extractFrames(url: string): Promise<ImageBitmap[]> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = async () => {
      const duration = video.duration;
      if (!duration || !isFinite(duration)) {
        resolve([]);
        return;
      }

      const thumbWidth = Math.round(THUMB_HEIGHT * (video.videoWidth / video.videoHeight));
      const canvas = document.createElement('canvas');
      canvas.width = thumbWidth;
      canvas.height = THUMB_HEIGHT;
      const ctx = canvas.getContext('2d')!;

      const thumbs: ImageBitmap[] = [];
      const count = Math.max(1, Math.ceil(duration / THUMB_INTERVAL_SEC));

      for (let i = 0; i < count; i++) {
        const time = i * THUMB_INTERVAL_SEC;
        try {
          await seekTo(video, time);
          ctx.drawImage(video, 0, 0, thumbWidth, THUMB_HEIGHT);
          const bitmap = await createImageBitmap(canvas);
          thumbs.push(bitmap);
        } catch {
          // Skip frames that fail to seek
        }
      }

      video.src = '';
      resolve(thumbs);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video for thumbnails'));
      video.src = '';
    };

    video.src = url;
  });
}

function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => resolve(), 2000); // safety timeout
    video.onseeked = () => {
      clearTimeout(timeout);
      resolve();
    };
    video.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('Seek failed'));
    };
    video.currentTime = time;
  });
}

export function getThumbnails(assetId: string): ImageBitmap[] | undefined {
  return thumbCache.get(assetId);
}

// ── Single-frame card thumbnail (WebCodecs → OffscreenCanvas → Blob URL) ──

const CARD_THUMB_WIDTH = 320;
const CARD_THUMB_HEIGHT = 200;

/** Session cache: assetId → blob URL of JPEG thumbnail */
const cardThumbCache = new Map<string, string>();

/**
 * Capture a single card thumbnail for the MediaBin grid.
 * Uses the proxy video in IDB → hidden video seek → OffscreenCanvas → JPEG blob URL.
 * Returns a blob URL that can be used as <img src>.
 */
export async function captureCardThumbnail(assetId: string): Promise<string | null> {
  const cached = cardThumbCache.get(assetId);
  if (cached) return cached;

  const proxyData = await getProxy(assetId);
  if (!proxyData) return null;

  const blob = new Blob([proxyData], { type: 'video/mp4' });
  const url = URL.createObjectURL(blob);

  try {
    const thumbUrl = await extractSingleFrame(url);
    if (thumbUrl) cardThumbCache.set(assetId, thumbUrl);
    return thumbUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function extractSingleFrame(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    const cleanup = () => { video.src = ''; };
    const timeout = setTimeout(() => { cleanup(); resolve(null); }, 5000);

    video.onloadedmetadata = async () => {
      // Seek to 10% or 1s, whichever is smaller
      const seekTime = Math.min(1, video.duration * 0.1);
      try {
        await seekTo(video, seekTime);
      } catch {
        clearTimeout(timeout);
        cleanup();
        resolve(null);
        return;
      }

      const canvas = new OffscreenCanvas(CARD_THUMB_WIDTH, CARD_THUMB_HEIGHT);
      const ctx = canvas.getContext('2d')!;

      // Letterbox: compute aspect-correct rect
      const srcAspect = video.videoWidth / video.videoHeight;
      const dstAspect = CARD_THUMB_WIDTH / CARD_THUMB_HEIGHT;
      let dw: number, dh: number, dx: number, dy: number;

      if (srcAspect > dstAspect) {
        dw = CARD_THUMB_WIDTH;
        dh = CARD_THUMB_WIDTH / srcAspect;
        dx = 0;
        dy = (CARD_THUMB_HEIGHT - dh) / 2;
      } else {
        dh = CARD_THUMB_HEIGHT;
        dw = CARD_THUMB_HEIGHT * srcAspect;
        dx = (CARD_THUMB_WIDTH - dw) / 2;
        dy = 0;
      }

      ctx.fillStyle = '#2A2A2E';
      ctx.fillRect(0, 0, CARD_THUMB_WIDTH, CARD_THUMB_HEIGHT);
      ctx.drawImage(video, dx, dy, dw, dh);

      clearTimeout(timeout);
      cleanup();

      const jpgBlob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.75 });
      resolve(URL.createObjectURL(jpgBlob));
    };

    video.onerror = () => {
      clearTimeout(timeout);
      cleanup();
      resolve(null);
    };

    video.src = url;
  });
}

export function getCardThumbnail(assetId: string): string | null {
  return cardThumbCache.get(assetId) ?? null;
}

export function clearThumbnails(assetId: string) {
  const thumbs = thumbCache.get(assetId);
  if (thumbs) {
    thumbs.forEach((t) => t.close());
    thumbCache.delete(assetId);
  }
}
